import { Screen, useTranslation, useUserSettings } from "@next-platform/client-common-core";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconVariants,
  Input,
  Size,
  Spinner,
  SpinnerSize,
  Switch,
  Tabs,
  Text,
  TextVariants,
  useMedia,
} from "@next-platform/client-common-spicy-ui";
import { JSX, useEffect, useMemo, useRef, useState } from "react";

import Trans from "@/components/Trans/Trans";
import { cn } from "@/utils";

import { VoiceCategories } from "../voices/types";
import { ChatVoiceSelectionListItem } from "./ChatVoiceSelectionListItem";
import { VoiceSelectionContentProps } from "./types";

export function VoiceSelectionContent({
  listItems,
  loadingList,
  defaultSelectedVoice,
  voiceSelectionLoading = false,
  isMobile,
  onSearch = () => {},
  stopAudio = () => {},
  onVoiceSelection = () => {},
  voiceCategoryList = [],
  selectedCategory,
  onPressCategory,
  characterDefaultVoiceId,
  dataTestId = "",
}: VoiceSelectionContentProps): JSX.Element {
  const { t } = useTranslation();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const { hoverNone } = useMedia();
  const [searchValue, setSearchValue] = useState("");
  const [selectedVoiceToPlay, setSelectedVoiceToPlay] = useState<string | null>(null);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const { ttsIncludeNarration, onUserSetTtsIncludeNarration } = useUserSettings();
  const [narrationToggleLoading, setNarrationToggleLoading] = useState(false);
  const [selectedVoiceId, setSelectedVoiceId] = useState<string | null>(null);

  const filteredListItems = useMemo(() => {
    if (!searchValue) {
      return listItems;
    }
    const normalizedSearchValue = searchValue.toLowerCase();
    return listItems?.filter((voiceItem) => {
      if (!voiceItem?.id) {
        return false;
      }
      const labelMatches = voiceItem.label?.toLowerCase()?.includes(normalizedSearchValue) ?? false;
      const subLabelMatches = voiceItem.subLabel?.toLowerCase()?.includes(normalizedSearchValue) ?? false;
      return labelMatches || subLabelMatches;
    });
  }, [listItems, searchValue]);
  const noResults = searchValue && !loadingList && filteredListItems?.length === 0;

  const updatedOnPressCategory = (tabName: string) => {
    onPressCategory?.(tabName as VoiceCategories);
    setPlayingIndex(null);
    setSelectedVoiceToPlay(null);
  };

  const handleOnChange = (value: string) => {
    setSearchValue(value);
    setPlayingIndex(null);
    onSearch?.(value);
  };

  const handleVoiceSelection = (voiceId: string) => {
    setSelectedVoiceToPlay(selectedVoiceToPlay === voiceId ? null : voiceId);
  };

  const confirmVoiceSelection = (voiceId: string) => {
    setSelectedVoiceId(voiceId);
    onVoiceSelection(voiceId);
  };

  const onUserConfirmVoiceSelection = (voiceId: string) => {
    const isDefault = voiceId === defaultSelectedVoice;
    if (!isDefault) {
      confirmVoiceSelection(voiceId);
    }
  };

  const isResetToDefaultVoiceVisible = !!characterDefaultVoiceId;
  const isResetToDefaultVoiceDisabled = !characterDefaultVoiceId || characterDefaultVoiceId === defaultSelectedVoice || voiceSelectionLoading;
  const isResetToDefaultVoiceLoading = voiceSelectionLoading ? selectedVoiceId === characterDefaultVoiceId || selectedVoiceId === "" : false;

  const resetPlayingIndex = () => {
    setPlayingIndex(null);
  };

  const handleTtsIncludeNarrationToggle = async () => {
    setNarrationToggleLoading(true);
    try {
      await onUserSetTtsIncludeNarration(!ttsIncludeNarration, Screen.CHAT);
    } finally {
      setNarrationToggleLoading(false);
    }
  };
  // used for consistence spacing on both side voice list scroller
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!hoverNone && scroller) {
      if (scroller.scrollHeight > scroller.clientHeight) {
        scroller.classList.add("!pr-2.5");
      } else {
        scroller.classList.remove("!pr-2.5");
      }
    }
  }, [hoverNone, filteredListItems]);

  return (
    <div className="flex flex-col gap-3.5 flex-shrink h-full w-full" data-testid={dataTestId}>
      <div className="flex flex-col gap-4 px-lg">
        {!!voiceCategoryList?.length && <Tabs tabItems={voiceCategoryList} activeTab={selectedCategory ?? ""} onTabChange={updatedOnPressCategory} />}
        <Input value={searchValue} placeholder={t("common:search")} iconStart={IconVariants.SEARCH} onChange={handleOnChange} />

        <div className="flex items-center justify-between px-1">
          <div>
            <Text variant={TextVariants.LABEL_MEDIUM_REGULAR}>
              <Trans i18nKey="chat:modal.voiceSelection.includeNarration.title" />
            </Text>
            <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-12">
              <Trans i18nKey="chat:modal.voiceSelection.includeNarration.label" />
            </Text>
          </div>
          <Switch
            checked={ttsIncludeNarration}
            onCheckedChange={handleTtsIncludeNarrationToggle}
            disabled={narrationToggleLoading}
            size={Size.LARGE}
          />
        </div>
        {isResetToDefaultVoiceVisible ? (
          <Button
            size={ButtonSize.LARGE}
            variant={ButtonVariant.SECONDARY}
            className="w-full"
            disabled={isResetToDefaultVoiceDisabled}
            loading={isResetToDefaultVoiceLoading}
            loadingWithText={false}
            onClick={() => {
              if (!characterDefaultVoiceId || isResetToDefaultVoiceDisabled) {
                return;
              }
              confirmVoiceSelection("");
            }}
          >
            Reset to Default Voice
          </Button>
        ) : null}
      </div>

      <div ref={scrollerRef} className={cn("flex flex-col overflow-y-auto h-full px-lg", !hoverNone && "custom-scroll")}>
        <div className="flex flex-col grow gap-md">
          {loadingList ? (
            <div className="grow flex items-center justify-center py-xl">
              <Spinner size={SpinnerSize.LARGE} className="mx-auto" />
            </div>
          ) : null}
          {noResults ? (
            <div className="grow grid place-items-center">
              <Text variant={TextVariants.HEADING_6_MEDIUM} className="text-gray-11">
                <Trans i18nKey="common:noResultsFound" />
              </Text>
            </div>
          ) : null}
          {!loadingList &&
            filteredListItems?.map((item, index) => {
              if (!item?.id) {
                return null;
              }

              return (
                <ChatVoiceSelectionListItem
                  {...item}
                  label={item.label ?? item.id}
                  key={`${item.id}-${index}`}
                  isPlaying={playingIndex === index}
                  isSelected={selectedVoiceId === item?.id}
                  isDefaultSelected={item?.id === defaultSelectedVoice}
                  onChange={(isPlay: boolean) => {
                    if (isMobile && playingIndex === index) {
                      stopAudio();
                      setPlayingIndex(null);
                      return;
                    }

                    setPlayingIndex(isPlay ? index : null);
                    item?.onChange?.(isPlay, resetPlayingIndex);
                    handleVoiceSelection(item?.id);
                  }}
                  onSelect={() => {
                    onUserConfirmVoiceSelection(item?.id);
                  }}
                  isSelectLoading={voiceSelectionLoading}
                />
              );
            })}
        </div>
      </div>
    </div>
  );
}
